import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, LogOut, FileText, X, ExternalLink, Eye, EyeOff, Tags } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api, formatApiError, LOGO_URL } from "@/lib/api";

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", category: "General", author: "Giva Team", published: true };
const EMPTY_CATEGORY = { key: "", title_en: "", title_pt: "", desc_en: "", desc_pt: "", icon_key: "wrench", sort_order: 0, visible: true };
const inputCls = "w-full rounded-xl border border-giva-line bg-giva-bg px-4 py-2.5 text-base outline-none transition-colors focus:border-giva-primary";
const Field = ({ label, children }) => (
  <div><label className="text-sm font-medium text-giva-text">{label}</label><div className="mt-2">{children}</div></div>
);

function PostEditor({ initial, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error(t("admin.editor.titleRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title, excerpt: form.excerpt, content: form.content,
        cover_image: form.cover_image, category: form.category || "General",
        author: form.author || "Giva Team", published: form.published,
      };
      if (isEdit) await api.put(`/admin/posts/${initial.id}`, payload);
      else await api.post("/admin/posts", payload);
      toast.success(isEdit ? t("admin.editor.updated") : t("admin.editor.created"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" data-testid="post-editor">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-giva-line px-6 py-4">
          <h2 className="font-display text-xl font-bold text-giva-navy">{isEdit ? t("admin.editor.editT") : t("admin.editor.newT")}</h2>
          <button onClick={onClose} data-testid="editor-close-btn" className="rounded-lg p-2 text-giva-text2 hover:bg-giva-surface2"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-5 p-6">
          <Field label={t("admin.editor.title")}>
            <input data-testid="editor-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder={t("admin.editor.titlePh")} />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t("admin.editor.category")}>
              <input data-testid="editor-category-input" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} placeholder={t("admin.editor.categoryPh")} />
            </Field>
            <Field label={t("admin.editor.author")}>
              <input data-testid="editor-author-input" value={form.author} onChange={(e) => set("author", e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label={t("admin.editor.cover")}>
            <input data-testid="editor-cover-input" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} className={inputCls} placeholder="https://..." />
          </Field>
          {form.cover_image && <img src={form.cover_image} alt={t("admin.editor.preview")} className="h-40 w-full rounded-xl object-cover" />}
          <Field label={t("admin.editor.excerpt")}>
            <textarea data-testid="editor-excerpt-input" rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className={`${inputCls} resize-none`} placeholder={t("admin.editor.excerptPh")} />
          </Field>
          <Field label={t("admin.editor.content")}>
            <textarea data-testid="editor-content-input" rows={8} value={form.content} onChange={(e) => set("content", e.target.value)} className={`${inputCls} resize-none`} placeholder={t("admin.editor.contentPh")} />
          </Field>
          <label className="flex items-center gap-3 text-sm font-medium text-giva-text">
            <input type="checkbox" data-testid="editor-published-checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="h-4 w-4 accent-giva-primary" />
            {t("admin.editor.publishedLabel")}
          </label>
        </div>
        <div className="flex justify-end gap-3 border-t border-giva-line px-6 py-4">
          <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-giva-text2 hover:bg-giva-surface2">{t("admin.editor.cancel")}</button>
          <button onClick={save} disabled={saving} data-testid="editor-save-btn" className="inline-flex items-center gap-2 rounded-full bg-giva-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-giva-primaryHover disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("admin.editor.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY_CATEGORY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title_en.trim() || !form.title_pt.trim()) {
      toast.error("English and Portuguese titles are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        key: form.key,
        title_en: form.title_en,
        title_pt: form.title_pt,
        desc_en: form.desc_en,
        desc_pt: form.desc_pt,
        icon_key: form.icon_key || "wrench",
        sort_order: Number(form.sort_order) || 0,
        visible: form.visible,
      };
      if (isEdit) await api.put(`/admin/service-categories/${initial.id}`, payload);
      else await api.post("/admin/service-categories", payload);
      toast.success(isEdit ? "Category updated" : "Category created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" data-testid="category-editor">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-giva-line px-6 py-4">
          <h2 className="font-display text-xl font-bold text-giva-navy">{isEdit ? "Edit category" : "New category"}</h2>
          <button onClick={onClose} className="rounded-lg p-2 text-giva-text2 hover:bg-giva-surface2"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-5 p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Key">
              <input value={form.key} onChange={(e) => set("key", e.target.value)} className={inputCls} placeholder="device-repair" />
            </Field>
            <Field label="Icon key">
              <input value={form.icon_key} onChange={(e) => set("icon_key", e.target.value)} className={inputCls} placeholder="gas, car, motorbike, device-repair..." />
            </Field>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Title (EN)"><input value={form.title_en} onChange={(e) => set("title_en", e.target.value)} className={inputCls} /></Field>
            <Field label="Title (PT)"><input value={form.title_pt} onChange={(e) => set("title_pt", e.target.value)} className={inputCls} /></Field>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Description (EN)"><textarea rows={3} value={form.desc_en} onChange={(e) => set("desc_en", e.target.value)} className={`${inputCls} resize-none`} /></Field>
            <Field label="Description (PT)"><textarea rows={3} value={form.desc_pt} onChange={(e) => set("desc_pt", e.target.value)} className={`${inputCls} resize-none`} /></Field>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Order"><input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className={inputCls} /></Field>
            <label className="mt-8 flex items-center gap-3 text-sm font-medium text-giva-text">
              <input type="checkbox" checked={form.visible} onChange={(e) => set("visible", e.target.checked)} className="h-4 w-4 accent-giva-primary" />
              Visible on landing page
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-giva-line px-6 py-4">
          <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-giva-text2 hover:bg-giva-surface2">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-giva-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-giva-primaryHover disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("posts");
  const [posts, setPosts] = useState(null);
  const [categories, setCategories] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/posts").then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([]));
    api.get("/admin/service-categories").then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => setCategories([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onLogout = () => { logout(); navigate("/admin/login"); };

  const remove = async (id) => {
    if (!window.confirm(t("admin.dash.confirmDelete"))) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success(t("admin.dash.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/admin/service-categories/${id}`);
      toast.success("Category deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openNew = () => { setEditing(null); setShowEditor(true); };
  const openEdit = (p) => { setEditing(p); setShowEditor(true); };
  const onSaved = () => { setShowEditor(false); load(); };
  const openNewCategory = () => { setEditingCategory(null); setShowCategoryEditor(true); };
  const openEditCategory = (c) => { setEditingCategory(c); setShowCategoryEditor(true); };
  const onCategorySaved = () => { setShowCategoryEditor(false); load(); };

  return (
    <div className="min-h-screen bg-giva-surface2">
      <header className="sticky top-0 z-40 border-b border-giva-line bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 lg:px-8">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Giva" className="h-9 w-9 rounded-lg bg-black object-contain" />
            <div>
              <p className="font-display text-base font-bold text-giva-navy">{t("admin.dash.panel")}</p>
              <p className="text-xs text-giva-text2">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" className="hidden items-center gap-2 rounded-full border border-giva-line px-4 py-2 text-sm font-medium text-giva-navy hover:bg-giva-surface2 sm:inline-flex">
              {t("admin.dash.viewSite")} <ExternalLink className="h-4 w-4" />
            </a>
            <button onClick={onLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full bg-giva-primary px-4 py-2 text-sm font-semibold text-white hover:bg-giva-primaryHover">
              <LogOut className="h-4 w-4" /> {t("admin.dash.logout")}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-giva-navy">{activeTab === "posts" ? t("admin.dash.title") : "Service Categories"}</h1>
            <p className="mt-1 text-giva-text2">
              {activeTab === "posts"
                ? (posts ? `${posts.length} ${t("admin.dash.articles")}` : t("admin.dash.loading"))
                : (categories ? `${categories.length} category(s)` : t("admin.dash.loading"))}
            </p>
          </div>
          {activeTab === "posts" ? (
            <button onClick={openNew} data-testid="new-post-btn" className="inline-flex items-center gap-2 rounded-full bg-giva-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-giva-accentHover">
              <Plus className="h-4 w-4" /> {t("admin.dash.new")}
            </button>
          ) : (
            <button onClick={openNewCategory} data-testid="new-category-btn" className="inline-flex items-center gap-2 rounded-full bg-giva-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-giva-accentHover">
              <Plus className="h-4 w-4" /> New category
            </button>
          )}
        </div>

        <div className="mt-8 flex gap-2 rounded-2xl border border-giva-line bg-white p-2">
          <button onClick={() => setActiveTab("posts")} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${activeTab === "posts" ? "bg-giva-primary text-white" : "text-giva-text2 hover:bg-giva-surface2"}`}>
            <FileText className="h-4 w-4" /> Blog
          </button>
          <button onClick={() => setActiveTab("categories")} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${activeTab === "categories" ? "bg-giva-primary text-white" : "text-giva-text2 hover:bg-giva-surface2"}`}>
            <Tags className="h-4 w-4" /> Categories
          </button>
        </div>

        {activeTab === "posts" && posts === null && <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-giva-primary" /></div>}

        {activeTab === "posts" && posts && posts.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-giva-line bg-white py-20 text-center" data-testid="no-posts">
            <FileText className="mx-auto h-10 w-10 text-giva-text2" />
            <p className="mt-4 font-display text-lg font-bold text-giva-navy">{t("admin.dash.emptyT")}</p>
            <p className="mt-1 text-giva-text2">{t("admin.dash.emptyD")}</p>
          </div>
        )}

        {activeTab === "posts" && posts && posts.length > 0 && (
          <div className="mt-8 overflow-hidden rounded-2xl border border-giva-line bg-white">
            <table className="w-full" data-testid="posts-table">
              <thead className="border-b border-giva-line bg-giva-surface2 text-left text-xs font-semibold uppercase tracking-wider text-giva-text2">
                <tr>
                  <th className="px-6 py-4">{t("admin.dash.colArticle")}</th>
                  <th className="hidden px-6 py-4 sm:table-cell">{t("admin.dash.colCategory")}</th>
                  <th className="hidden px-6 py-4 md:table-cell">{t("admin.dash.colStatus")}</th>
                  <th className="px-6 py-4 text-right">{t("admin.dash.colActions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-giva-line">
                {posts.map((p) => (
                  <tr key={p.id} data-testid={`post-row-${p.slug}`} className="hover:bg-giva-bg">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {p.cover_image
                          ? <img src={p.cover_image} alt="" className="h-11 w-11 rounded-lg object-cover" />
                          : <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-giva-surface2"><FileText className="h-5 w-5 text-giva-text2" /></span>}
                        <div className="min-w-0">
                          <p className="truncate font-medium text-giva-navy">{p.title}</p>
                          <p className="truncate text-xs text-giva-text2">{p.author}</p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-6 py-4 sm:table-cell">
                      <span className="rounded-full bg-giva-surface2 px-3 py-1 text-xs font-medium text-giva-text">{p.category}</span>
                    </td>
                    <td className="hidden px-6 py-4 md:table-cell">
                      {p.published
                        ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700"><Eye className="h-3.5 w-3.5" /> {t("admin.dash.published")}</span>
                        : <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-giva-text2"><EyeOff className="h-3.5 w-3.5" /> {t("admin.dash.draft")}</span>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(p)} data-testid={`edit-post-${p.slug}`} className="rounded-lg p-2 text-giva-text2 hover:bg-giva-surface2 hover:text-giva-primary"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(p.id)} data-testid={`delete-post-${p.slug}`} className="rounded-lg p-2 text-giva-text2 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "categories" && categories === null && <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-giva-primary" /></div>}

        {activeTab === "categories" && categories && (
          <div className="mt-8 overflow-hidden rounded-2xl border border-giva-line bg-white">
            <table className="w-full" data-testid="categories-table">
              <thead className="border-b border-giva-line bg-giva-surface2 text-left text-xs font-semibold uppercase tracking-wider text-giva-text2">
                <tr>
                  <th className="px-6 py-4">Category</th>
                  <th className="hidden px-6 py-4 sm:table-cell">Icon / Order</th>
                  <th className="hidden px-6 py-4 md:table-cell">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-giva-line">
                {categories.map((c) => (
                  <tr key={c.id} data-testid={`category-row-${c.key}`} className="hover:bg-giva-bg">
                    <td className="px-6 py-4">
                      <p className="font-medium text-giva-navy">{c.title_en} <span className="text-giva-text2">/ {c.title_pt}</span></p>
                      <p className="mt-1 line-clamp-1 text-xs text-giva-text2">{c.desc_en || c.desc_pt}</p>
                    </td>
                    <td className="hidden px-6 py-4 sm:table-cell">
                      <span className="rounded-full bg-giva-surface2 px-3 py-1 text-xs font-medium text-giva-text">{c.icon_key} · {c.sort_order}</span>
                    </td>
                    <td className="hidden px-6 py-4 md:table-cell">
                      {c.visible
                        ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700"><Eye className="h-3.5 w-3.5" /> Visible</span>
                        : <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-giva-text2"><EyeOff className="h-3.5 w-3.5" /> Hidden</span>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEditCategory(c)} data-testid={`edit-category-${c.key}`} className="rounded-lg p-2 text-giva-text2 hover:bg-giva-surface2 hover:text-giva-primary"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => removeCategory(c.id)} data-testid={`delete-category-${c.key}`} className="rounded-lg p-2 text-giva-text2 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showEditor && <PostEditor initial={editing} onClose={() => setShowEditor(false)} onSaved={onSaved} />}
      {showCategoryEditor && <CategoryEditor initial={editingCategory} onClose={() => setShowCategoryEditor(false)} onSaved={onCategorySaved} />}
    </div>
  );
}
